import { create } from '@thoughtspot/logger';
import { ChartConfigMode, AxisType } from '@thoughtspot/ts-chart-sdk';
import Joi from 'joi';
import _ from 'lodash';
import { ChartColumnSchema } from '../../common/joi-validation-schemas';
import { validateSDKChartConfigEditorDefinition } from '../validate-chart-config/util';
import { validateSDKVisualPropEditorDefinition } from '../validate-visual-props/util';
const logger = create('initialize-custom-chart/util');
const flatAxisSchema = Joi.object({
    type: Joi.string().valid('flat').required(),
    column: ChartColumnSchema.required(),
}).unknown(false);
const mergedAxisSchema = Joi.object({
    type: Joi.string().valid('merged').required(),
    columns: Joi.array()
        .items(ChartColumnSchema)
        .min(1)
        .required(),
}).unknown(false);
const dualAxisSchema = Joi.object({
    type: Joi.string().valid('dual').required(),
    primary: Joi.alternatives().try(flatAxisSchema, mergedAxisSchema).required(),
    secondary: Joi.alternatives().try(flatAxisSchema, mergedAxisSchema).required(),
}).unknown(false);
const customChartAxisConfigSchema = Joi.alternatives().try(flatAxisSchema, mergedAxisSchema, dualAxisSchema);
const sdkChartConfigDimension = Joi.object({
    key: Joi.string().required(),
    mode: Joi.string().valid('COLUMN_DRIVEN', 'AXIS_DRIVEN').optional(),
    columns: Joi.array()
        .items(ChartColumnSchema)
        .min(0),
    axes: Joi.array()
        .items(customChartAxisConfigSchema)
        .min(0),
}).unknown(false);
const sdkChartConfigSchema = Joi.object({
    key: Joi.string().required(),
    dimensions: Joi.array()
        .items(sdkChartConfigDimension)
        .min(0),
}).unknown(false);
export const validateColumnIdList = (sdkColumnList, columns) => {
    sdkColumnList.forEach(sdkColumn => {
        const idx = columns.findIndex(col => col.id === sdkColumn.id);
        if (idx === -1) {
            logger.error('columnId List is invalid. columnId not found in the model.', sdkColumn);
            throw new Error(`columnId List is invalid. column with id:${sdkColumn} not found in the model.`);
        }
    });
};
export const extractColumnsFromDimension = (dimension) => {
    const columns = [];
    if (dimension.mode === ChartConfigMode.COLUMN_DRIVEN) {
        columns.push(...dimension.columns ?? []);
    }
    if (dimension.mode === ChartConfigMode.AXIS_DRIVEN) {
        dimension.axes.forEach((axisConfig) => {
            if (axisConfig.type === AxisType.FLAT) {
                columns.push(axisConfig.column);
            }
            else if (axisConfig.type === AxisType.MERGED && axisConfig.columns) {
                columns.push(...axisConfig.columns);
            }
            else if (axisConfig.type === AxisType.DUAL) {
                if (axisConfig.primary.type === AxisType.FLAT && axisConfig.primary.column) {
                    columns.push(axisConfig.primary.column);
                }
                else if (axisConfig.primary.type === AxisType.MERGED && axisConfig.primary.columns) {
                    columns.push(...axisConfig.primary.columns);
                }
                if (axisConfig.secondary.type === AxisType.FLAT && axisConfig.secondary.column) {
                    columns.push(axisConfig.secondary.column);
                }
                else if (axisConfig.secondary.type === AxisType.MERGED && axisConfig.secondary.columns) {
                    columns.push(...axisConfig.secondary.columns);
                }
            }
        });
    }
    return columns;
};
const validateSDKChartConfig = (sdkChartConfigList, columns) => {
    try {
        if (!_.isArray(sdkChartConfigList)) {
            throw new Error('sdkChartConfig is not an array');
        }
        _.each(sdkChartConfigList, sdkChartConfig => {
            Joi.assert(sdkChartConfig, sdkChartConfigSchema);
            _.each(sdkChartConfig.dimensions, dimension => {
                const extractedColumns = extractColumnsFromDimension(dimension);
                validateColumnIdList(extractedColumns, columns);
            });
        });
    }
    catch (err) {
        logger.error('sdkChartConfig is invalid', err);
        return false;
    }
    return true;
};
export const validateInitializeEventResponsePayload = (payload, columns) => {
    const { isConfigValid, defaultChartConfig: defaultSDKChartConfig, chartConfigEditorDefinition, visualPropEditorDefinition, } = payload;
    const errorList = [];
    if (_.isNil(isConfigValid) || !_.isBoolean(isConfigValid)) {
        errorList.push('isConfigValid is not a boolean or is undefined');
    }
    if (!_.isNil(defaultSDKChartConfig) &&
        !validateSDKChartConfig(defaultSDKChartConfig, columns)) {
        errorList.push('defaultChartConfig is invalid');
    }
    if (!_.isNil(chartConfigEditorDefinition) &&
        !validateSDKChartConfigEditorDefinition(chartConfigEditorDefinition, logger)) {
        errorList.push('chartConfigEditorDefinition is invalid');
    }
    if (!_.isEmpty(visualPropEditorDefinition) &&
        !validateSDKVisualPropEditorDefinition(visualPropEditorDefinition, logger)) {
        errorList.push('visualPropEditorDefinition is invalid');
    }
    if (errorList.length > 0) {
        return {
            isValid: false,
            validationErrorMessage: errorList,
        };
    }
    return {
        isValid: true,
    };
};
export const testHooks = {
    validateSDKChartConfig,
    validateSDKChartConfigEditorDefinition,
    extractColumnsFromDimension,
};
export { flatAxisSchema, mergedAxisSchema, dualAxisSchema, customChartAxisConfigSchema, sdkChartConfigDimension, };
//# sourceMappingURL=util.js.map